import { useEffect, useState, type FormEvent } from "react";
import { PageHeader, Panel } from "../components/Surface";
import { Button } from "../components/ui/Button";
import { inputClass } from "../components/FormKit";
import {
  addPurchase, listPurchases, deletePurchase, setMiles, listMiles, iftaSummary, quarterOf,
  FUEL_TYPES, FUEL_LABELS,
  type FuelPurchase, type MileRow, type IftaSummary, type FuelType,
} from "../lib/fuel";

const money = (c: number | null) => (c == null ? "" : `$${(c / 100).toFixed(2)}`);
const gal = (n: number) => `${n.toLocaleString(undefined, { maximumFractionDigits: 3 })} gal`;

export function Fuel() {
  const [quarter, setQuarter] = useState(quarterOf());
  const [purchases, setPurchases] = useState<FuelPurchase[]>([]);
  const [miles, setMilesState] = useState<MileRow[]>([]);
  const [ifta, setIfta] = useState<IftaSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [pForm, setPForm] = useState({ gallons: "", jurisdiction: "", fuel_type: "diesel" as FuelType, total: "", vendor: "" });
  const [mForm, setMForm] = useState({ jurisdiction: "", miles: "" });

  async function refresh(q: string) {
    const [p, m, s] = await Promise.all([listPurchases(q), listMiles(q), iftaSummary(q)]);
    setPurchases(p.purchases); setMilesState(m.miles); setIfta(s);
  }
  useEffect(() => { refresh(quarter).finally(() => setLoading(false)); }, [quarter]);

  async function addFuel(e: FormEvent) {
    e.preventDefault();
    if (!pForm.gallons) return;
    setBusy(true);
    try {
      await addPurchase({
        gallons: Number(pForm.gallons), jurisdiction: pForm.jurisdiction || undefined, fuel_type: pForm.fuel_type,
        total_cents: pForm.total ? Math.round(Number(pForm.total) * 100) : undefined, vendor: pForm.vendor || undefined,
      });
      setPForm({ gallons: "", jurisdiction: "", fuel_type: "diesel", total: "", vendor: "" });
      await refresh(quarter);
    } finally { setBusy(false); }
  }
  async function saveMiles(e: FormEvent) {
    e.preventDefault();
    if (!mForm.jurisdiction || !mForm.miles) return;
    setBusy(true);
    try { await setMiles({ quarter, jurisdiction: mForm.jurisdiction, miles: Number(mForm.miles) }); setMForm({ jurisdiction: "", miles: "" }); await refresh(quarter); }
    finally { setBusy(false); }
  }
  async function removePurchase(id: string) {
    setBusy(true);
    try { await deletePurchase(id); await refresh(quarter); } finally { setBusy(false); }
  }

  const quarters = [0, 1, 2, 3].map((back) => {
    const d = new Date(); d.setUTCMonth(d.getUTCMonth() - back * 3); return quarterOf(d);
  }).filter((v, i, a) => a.indexOf(v) === i);

  return (
    <div className="mx-auto grid w-full max-w-3xl gap-6">
      <PageHeader
        title="Fuel & IFTA"
        subtitle="Log fuel purchases and per-state miles; we compute your quarterly IFTA taxable gallons per jurisdiction."
        actions={
          <select className={inputClass} value={quarter} onChange={(e) => setQuarter(e.target.value)} aria-label="Quarter" data-testid="quarter">
            {quarters.map((q) => <option key={q} value={q}>{q}</option>)}
          </select>
        }
      />

      <Panel title="Log a fuel purchase">
        <form className="grid gap-2 sm:grid-cols-5" onSubmit={addFuel}>
          <input className={inputClass} type="number" step="0.001" placeholder="Gallons" value={pForm.gallons} onChange={(e) => setPForm({ ...pForm, gallons: e.target.value })} aria-label="Gallons" />
          <input className={inputClass} maxLength={2} placeholder="State" value={pForm.jurisdiction} onChange={(e) => setPForm({ ...pForm, jurisdiction: e.target.value })} aria-label="Fuel state" />
          <select className={inputClass} value={pForm.fuel_type} onChange={(e) => setPForm({ ...pForm, fuel_type: e.target.value as FuelType })} aria-label="Fuel type">
            {FUEL_TYPES.map((t) => <option key={t} value={t}>{FUEL_LABELS[t]}</option>)}
          </select>
          <input className={inputClass} type="number" step="0.01" placeholder="Total $" value={pForm.total} onChange={(e) => setPForm({ ...pForm, total: e.target.value })} aria-label="Total cost" />
          <input className={inputClass} placeholder="Vendor" value={pForm.vendor} onChange={(e) => setPForm({ ...pForm, vendor: e.target.value })} />
          <Button type="submit" disabled={busy} className="sm:col-span-5" data-testid="add-fuel">Add purchase</Button>
        </form>
      </Panel>

      <Panel title="Miles by jurisdiction">
        <form className="mb-3 grid gap-2 sm:grid-cols-3" onSubmit={saveMiles}>
          <input className={inputClass} maxLength={2} placeholder="State (TX)" value={mForm.jurisdiction} onChange={(e) => setMForm({ ...mForm, jurisdiction: e.target.value })} aria-label="Miles state" />
          <input className={inputClass} type="number" placeholder="Miles this quarter" value={mForm.miles} onChange={(e) => setMForm({ ...mForm, miles: e.target.value })} aria-label="Miles" />
          <Button type="submit" disabled={busy} data-testid="set-miles">Set miles</Button>
        </form>
        {miles.length > 0 && (
          <p className="font-mono text-[11px] text-muted" data-testid="miles-list">
            {miles.map((m) => `${m.jurisdiction} ${m.miles.toLocaleString()}mi`).join(" · ")}
          </p>
        )}
      </Panel>

      {loading ? (
        <p className="font-mono text-sm text-muted">Loading…</p>
      ) : (
        <Panel title={`IFTA summary — ${quarter}`}>
          {ifta && ifta.rows.length > 0 ? (
            <div className="overflow-x-auto">
              <p className="mb-2 font-mono text-[11px] text-muted" data-testid="ifta-mpg">Fleet average: {ifta.mpg} MPG · {ifta.totals.miles.toLocaleString()} mi · {gal(ifta.totals.purchased_gallons)} bought</p>
              <table className="w-full text-sm" data-testid="ifta-table">
                <thead>
                  <tr className="border-b border-line text-left font-mono text-[11px] uppercase text-muted">
                    <th className="py-1 pr-3">Jur</th><th className="py-1 pr-3 text-right">Miles</th>
                    <th className="py-1 pr-3 text-right">Taxable gal</th><th className="py-1 pr-3 text-right">Bought gal</th>
                    <th className="py-1 text-right">Net gal</th>
                  </tr>
                </thead>
                <tbody className="font-mono tabular-nums">
                  {ifta.rows.map((r) => (
                    <tr key={r.jurisdiction} className="border-b border-line last:border-0">
                      <td className="py-1 pr-3 font-bold">{r.jurisdiction}</td>
                      <td className="py-1 pr-3 text-right">{r.miles.toLocaleString()}</td>
                      <td className="py-1 pr-3 text-right">{r.taxable_gallons}</td>
                      <td className="py-1 pr-3 text-right">{r.purchased_gallons}</td>
                      <td className={`py-1 text-right font-bold ${r.net_gallons > 0 ? "text-danger" : "text-verified"}`}>{r.net_gallons > 0 ? "+" : ""}{r.net_gallons}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-2 font-mono text-[10px] text-muted">Net gallons: positive = owe tax in that state, negative = credit. Estimate only — confirm with your filing service.</p>
            </div>
          ) : (
            <p className="font-mono text-sm text-muted">Add fuel purchases and per-state miles to see the IFTA summary.</p>
          )}
        </Panel>
      )}

      {purchases.length > 0 && (
        <Panel title={`Fuel purchases (${purchases.length})`}>
          <ul className="grid gap-2" data-testid="purchase-list">
            {purchases.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-3 border-b border-line pb-2 text-sm last:border-0 last:pb-0">
                <span className="font-mono text-[11px] text-muted">
                  {p.purchased_at} · {p.jurisdiction ?? "—"} · {gal(Number(p.gallons))} {FUEL_LABELS[p.fuel_type]}{p.total_cents != null ? ` · ${money(p.total_cents)}` : ""}{p.vendor ? ` · ${p.vendor}` : ""}
                </span>
                <button type="button" className="font-mono text-[11px] text-danger hover:underline" disabled={busy} onClick={() => removePurchase(p.id)}>Delete</button>
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </div>
  );
}
